import React, { useState, useRef } from 'react';
import SatelliteAnalysis from './components/SatelliteAnalysis';
import VerificationPortal from './components/VerificationPortal';
import ThirdPartyVerification from './components/ThirdPartyVerification';
import ApprovalAnimation from './components/ApprovalAnimation';
import BlockchainRegistry from './components/BlockchainRegistry';
import SmartContracts from './components/SmartContracts';
import CarbonMarketplace from './components/CarbonMarketplace';
import ImpactDashboard from './components/ImpactDashboard';
import './App.css';

function App() {
  const [currentPage, setCurrentPage] = useState('upload'); // 'upload', 'analysis', 'verification', 'thirdparty', 'blockchain', 'smartcontracts', 'marketplace', 'dashboard'
  const [showApprovalAnimation, setShowApprovalAnimation] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [coordinates, setCoordinates] = useState({
    latitude: '12.9716° 00.742',
    longitude: '77.5946°'
  });
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const updateLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude.toFixed(4);
          const lng = position.coords.longitude.toFixed(4);
          setCoordinates({
            latitude: `${lat}°`,
            longitude: `${lng}°`
          });
          alert('Location updated successfully!');
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Error getting location. Please try again.');
        }
      );
    } else {
      alert('GPS not supported by your browser.');
    }
  };

  const uploadToAnalysis = () => {
    if (selectedFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }
    
    // Navigate to analysis page
    console.log('Files to upload:', selectedFiles);
    console.log('Location data:', coordinates);
    setCurrentPage('analysis');
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleBackToUpload = () => {
    setCurrentPage('upload');
  };

  const handleSubmitVerification = () => {
    setCurrentPage('verification');
  };

  const handleBackToAnalysis = () => {
    setCurrentPage('analysis');
  };

  const handleGoToThirdParty = () => {
    setCurrentPage('thirdparty');
  };

  const handleApproveThirdParty = () => {
    setShowApprovalAnimation(true);
  };

  const handleRequestData = () => {
    alert('Additional data request sent to the submitter.');
  };

  const handleAnimationComplete = () => {
    setShowApprovalAnimation(false);
    setCurrentPage('blockchain');
  };

  const handleBlockchainNext = () => {
    setCurrentPage('smartcontracts');
  };

  const handleSmartContractsNext = () => {
    setCurrentPage('marketplace');
  };

  const handleMarketplaceNext = () => {
    setCurrentPage('dashboard');
  };

  const handleDashboardStartNew = () => {
    setCurrentPage('upload');
    setSelectedFiles([]);
  };

  // Render Approval Animation
  if (showApprovalAnimation) {
    return <ApprovalAnimation onComplete={handleAnimationComplete} />;
  }

  // Render Impact Dashboard Page
  if (currentPage === 'dashboard') {
    return <ImpactDashboard onStartNew={handleDashboardStartNew} />;
  }

  // Render Carbon Marketplace Page
  if (currentPage === 'marketplace') {
    return <CarbonMarketplace onNext={handleMarketplaceNext} />;
  }

  // Render Smart Contracts Page
  if (currentPage === 'smartcontracts') {
    return <SmartContracts onNext={handleSmartContractsNext} />;
  }

  // Render Blockchain Registry Page
  if (currentPage === 'blockchain') {
    return <BlockchainRegistry onNext={handleBlockchainNext} />;
  }

  // Render Third Party Verification Page
  if (currentPage === 'thirdparty') {
    return (
      <ThirdPartyVerification 
        onApprove={handleApproveThirdParty}
        onRequestData={handleRequestData}
      />
    );
  }

  // Render Verification Portal Page
  if (currentPage === 'verification') {
    return (
      <VerificationPortal 
        onBack={handleBackToAnalysis}
        onApprove={handleGoToThirdParty}
      />
    );
  }

  // Render Analysis Page
  if (currentPage === 'analysis') {
    return (
      <SatelliteAnalysis 
        onBack={handleBackToUpload} 
        onSubmitVerification={handleSubmitVerification}
      />
    );
  }

  // Render Upload Page
  return (
    <div className="blue-carbon-monitor">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <h1 className="title">Blue Carbon Monitor</h1>
          <p className="subtitle">Mangrove Restoration Site</p>
          <button className="new-profile-btn">📋 New Profile data</button>
        </div>
      </header>

      {/* Main Content */}
      <div className="main-container">
        {/* Sidebar */}
        <aside className="sidebar">
          {/* Photo Documentation Section */}
          <div className="section">
            <div className="section-header">
              <span className="section-icon">📷</span>
              <span className="section-title">Photo Documentation</span>
            </div>
          </div>

          {/* Location Data Section */}
          <div className="section">
            <div className="section-header">
              <span className="section-icon">📍</span>
              <span className="section-title">Location Data</span>
            </div>
            <div className="location-info">
              <div className="coordinate-row">
                <span className="label">Coordinates:</span>
              </div>
              <div className="coordinate-row">
                <span className="value">{coordinates.latitude}</span>
              </div>
              <div className="coordinate-row">
                <span className="value">{coordinates.longitude}</span>
              </div>
              <button className="update-location-btn" onClick={updateLocation}>
                🔄 Update Location
              </button>
            </div>
          </div>

          {/* Collection Status Section */}
          <div className="section">
            <div className="section-header">
              <span className="section-icon">📊</span>
              <span className="section-title">Collection Status</span>
            </div>
            <div className="status-info">
              <div className="status-item">
                <span className="status-label">Points collected:</span>
              </div>
              <div className="status-item">
                <span className="status-indicator green">●</span>
                <span className="status-text">GPS coordinates</span>
              </div>
              <div className="status-item">
                <span className="status-indicator green">●</span>
                <span className="status-text">Satellite.xml</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="content-area">
          <div 
            className="upload-area"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={openFileDialog}
          >
            <div className="upload-content">
              <div className="camera-icon">📷</div>
              <p className="upload-text">Tap to capture image or photo</p>
              {selectedFiles.length > 0 && (
                <div className="selected-files">
                  <p>{selectedFiles.length} file(s) selected</p>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="file-item">
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </main>
      </div>

      {/* Bottom Upload Button */}
      <div className="bottom-section">
        <button className="upload-analysis-btn" onClick={uploadToAnalysis}>
          📤 Upload to Analysis
        </button>
      </div>
    </div>
  );
}

export default App;
